import { ChangeEvent, ReactElement, useContext, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem, Tab, Tabs,
  TextField,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChartType,
  DashboardAggregateType,
  DashboardModuleType,
  DashboardOrderType,
  DashboardSchemaType,
  ICell,
  IDataSchema
} from "../types";
import { cloneObject } from "../../../system/utils";
import SeriesTable from "./seriesTable";
import { useSelector } from "react-redux";
import { ISelectedNode, IState, NullValue } from "../../../types";
import { SystemContext } from "../../../context/systemContext";
import GroupTable from "./groupTable";
import { getPalette } from "../../../configuration/configuration";
import { getMachinesByNode, getSchema } from "./utils";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TopPanel from "./topPanel";
import TabPanel from "../../../components/tabPanel";
import FilterTable from "./filterTable";
import StylePanel from "./stylePanel";
import SortPanel from "./sortPanel";

interface IProps {
  cell: ICell;
  open: boolean;
  setOpen: (open: boolean) => void;
  schemas: Record<DashboardSchemaType, any>;
}

const DialogFilter = ({ cell, open, setOpen, schemas }: IProps): ReactElement => {
  const systemContext = useContext(SystemContext);
  const darkMode = useSelector((state: IState) => state.darkMode);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [  machines  ] = useState<ISelectedNode[]>(getMachinesByNode(selectedNode));
  const [ editableCell, setEditableCell ] = useState<ICell>(cloneObject(cell));
  const [ schema, setSchema ] = useState<IDataSchema>(getSchema(schemas, cell.module ?? DashboardModuleType.Delay));
  const [ selectedTab, setSelectedTab ] = useState(0);


  useEffect(() => {
    const newCellData = cloneObject(cell);

    if(newCellData.machine === null && machines.length === 1){
      newCellData.machine = machines[0].code;
    }

    if(!newCellData.filters){
      newCellData.filters = [];
    }

    setEditableCell(newCellData);
    setSchema(getSchema(schemas, cell.module ?? DashboardModuleType.Delay));
  }, [ cell, machines ]);

  const onAccept = () => {
    cell.title = editableCell.title;
    cell.module = editableCell.module;
    cell.chart = editableCell.chart;
    cell.machine = editableCell.machine;
    cell.shift = editableCell.shift;
    cell.crew = editableCell.crew;
    cell.dateRange = editableCell.dateRange;
    cell.dimension = editableCell.dimension;
    cell.groups = editableCell.groups;
    cell.variables = editableCell.variables;
    cell.top = editableCell.top;
    cell.filterBy = editableCell.filterBy ?? "DIMENSION";
    cell.orderAsc = editableCell.orderAsc;
    cell.filters = editableCell.filters;
    cell.style = editableCell.style ?? {};
    cell.sort = editableCell.sort ?? { orderBy: "DIMENSION", orderAsc: false };
    setOpen(false);
  };

  const onCancel = () => {
    setOpen(false);
  };

  const onchangeFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setEditableCell({ ...editableCell, title: event.target.value });
  };

  const setChart = (value: string) => {
    const allowMultipleVariablesX = value === "SUMMARY" || value === "LINE";

    setEditableCell({
      ...editableCell,
      chart: value as ChartType,
      variables: allowMultipleVariablesX ? editableCell.variables : [ editableCell.variables[0] ]
    });
  };

  const updateCharConfig = (field: string, value: any) => {
    if (field === "module") {
      if (value !== editableCell.module) {
        const newSchema = getSchema(schemas, value);
        const keys = Object.keys(newSchema.variables);
        const varData = newSchema.variables[keys[0]];

        setEditableCell({ ...editableCell,
          module: value,
          dimension: "Factory Day",
          groups: [{
            field: "Machine",
            order: DashboardOrderType.Asc,
          }],
          variables: [{
            field: keys[0],
            aggregate: DashboardAggregateType.Sum,
            color: getPalette().list[2],
            unit: varData.units ? varData.units[0] : null,
            decimals: varData.decimals
          }]
        });

        setSchema(newSchema);
      }
    } else {
      setEditableCell({ ...editableCell, [field]: value === NullValue ? null : value });
    }
  };


  const handleChange = (_event: ChangeEvent<any>, newValue: number) => {
    setSelectedTab(newValue);
  };

  const allowMultipleVariables = editableCell.chart === "SUMMARY" || editableCell.chart === "LINE";

  return (
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={open} maxWidth="md">

      <DialogTitle id="customized-dialog-title">
        Chart Config
      </DialogTitle>

      <DialogContent dividers={true} >
        <div className="display-flex-row flex-wrap" style={{ width: 656 }}>
          <TextField
            variant="standard"
            sx={{ margin: 1, minWidth: 284, width: 284 }}
            placeholder={"Title"}
            label={"Title"}
            value={editableCell.title}
            onChange={onchangeFilter}/>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>Module</InputLabel>
            <Select
              variant="standard"
              value={editableCell.module ?? DashboardModuleType.Delay}
              displayEmpty={true}
              onChange={ event => updateCharConfig("module", event.target.value as DashboardModuleType) }>
              <MenuItem value={DashboardModuleType.Delay}>Delay</MenuItem>
              <MenuItem value={DashboardModuleType.Production}>Production</MenuItem>
              <MenuItem value={DashboardModuleType.Consumption}>Consumption</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>Chart</InputLabel>
            <Select
              variant="standard"
              value={editableCell.chart}
              displayEmpty={true}
              onChange={event => setChart(event.target.value as string)}>
              <MenuItem value="BAR">Bar</MenuItem>
              <MenuItem value="STACKED_BAR">Stacked Bar</MenuItem>
              <MenuItem value="LINE">Line</MenuItem>
              <MenuItem value="STACKED_LINE">Stacked Line</MenuItem>
              <MenuItem value="RADAR">Radar</MenuItem>
              <MenuItem value="PIE">Pie</MenuItem>
              <MenuItem value="TREEMAP">TreeMap</MenuItem>
              <MenuItem value="HEATMAP">HeatMap</MenuItem>
              <MenuItem value="SUMMARY">Summary</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel id="machine-label" sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>{systemContext.language.machine}</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={editableCell.machine ?? NullValue}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateCharConfig("machine", event.target.value)}>
              { machines.map((child) => {
                return <MenuItem key={`child-${child.nodeID}`} value={child.code}>{child.name}</MenuItem>;
              })
              }
              { machines.length > 1 && <MenuItem value={NullValue}>{systemContext.language.all}</MenuItem> }
            </Select>
          </FormControl>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>Date Filter</InputLabel>
            <Select
              variant="standard"
              value={editableCell.dateRange}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateCharConfig("dateRange", event.target.value)}>
              <MenuItem value="CURRENT_DAY">Current day</MenuItem>
              <MenuItem value="PREVIOUS_DAY">Previous day</MenuItem>
              <MenuItem value="CURRENT_WEEK">Current week</MenuItem>
              <MenuItem value="PREVIOUS_WEEK">Previous week</MenuItem>
              <MenuItem value="CURRENT_MONTH">Current month</MenuItem>
              <MenuItem value="PREVIOUS_MONTH">Previous month</MenuItem>
              <MenuItem value="LAST_3_MONTH">Last 3 months</MenuItem>
              <MenuItem value="LAST_6_MONTH">Last 6 months</MenuItem>
              <MenuItem value="LAST_9_MONTH">Last 9 months</MenuItem>
              <MenuItem value="LAST_12_MONTH">Last 12 months</MenuItem>
              <MenuItem value="CURRENT_YEAR">Current year</MenuItem>
              <MenuItem value="PREVIOUS_YEAR">Previous year</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel id="shift-select-label" sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>{systemContext.language.shift}</InputLabel>
            <Select
              variant="standard"
              labelId="shift-select-label"
              id="shift-simple-select"
              value={editableCell.shift ?? NullValue}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateCharConfig("shift", event.target.value)}>
              {systemContext.shifts.map((shiftItem) => {
                return <MenuItem key={`shift-${shiftItem.shiftID}`} value={shiftItem.code}>{shiftItem.name}</MenuItem>;
              })
              }
              <MenuItem value={NullValue}>{systemContext.language.all}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel id="crew-select-label" sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>{systemContext.language.crew}</InputLabel>
            <Select
              variant="standard"
              labelId="crew-select-label"
              id="crew-simple-select"
              value={editableCell.crew ?? NullValue}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateCharConfig("crew", event.target.value)}>
              {systemContext.crews.map((crewItem) => {
                return <MenuItem key={`crew-${crewItem.crewID}`} value={crewItem.code}>{crewItem.name}</MenuItem>;
              })
              }
              <MenuItem value={NullValue}>{systemContext.language.all}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ margin: 1, minWidth: 592, width: 592 }}>
            <InputLabel sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>Dimension</InputLabel>
            <Select
              variant="standard"
              value={editableCell.dimension}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateCharConfig("dimension", event.target.value)}>
              {Object.keys(schema.dimensions).map(field => <MenuItem key={`grouping-field-${field}`} value={field}>{field}</MenuItem>)}
            </Select>
          </FormControl>

          <Tabs
            value={selectedTab}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            centered={true}
            style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)", minWidth: 656, width: 656, marginTop: "1em"  }}>
            <Tab label="Group"/>
            <Tab label="Variable"/>
            <Tab label="Filter"/>
            <Tab label="Order" disabled={editableCell.chart !== "BAR"}/>
            <Tab label="Styles"/>
          </Tabs>

          <TabPanel value={selectedTab} index={0}>
            <GroupTable groups={editableCell.groups} schema={schema}
              updateGroups={(list) => updateCharConfig("groups", list)} />
          </TabPanel>

          <TabPanel value={selectedTab} index={1}>
            <SeriesTable variables={editableCell.variables} schema={schema} allowMultipleVariables={allowMultipleVariables}
              updateVariables={(list) => updateCharConfig("variables", list)} />
          </TabPanel>

          <TabPanel value={selectedTab} index={2}>
            <TopPanel updateCharConfig={updateCharConfig} editableCell={editableCell}/>
            <FilterTable filters={editableCell.filters ?? []} schema={schema}
              updateFilter={list => updateCharConfig("filters", list)} />
          </TabPanel>

          <TabPanel value={selectedTab} index={3}>
            <SortPanel updateSortConfig={(value) => updateCharConfig("sort", value)} sortData={editableCell.sort ?? { orderBy: "DIMENSION", orderAsc: false }}/>
          </TabPanel>

          <TabPanel value={selectedTab} index={4}>
            <StylePanel style={editableCell.style ?? {}}  updateStyle={value => updateCharConfig("style", value)}/>
          </TabPanel>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogFilter;
